// ---------------------------------------------------------------------------
// MiniPostsChart - Sparkline trend line with month labels and tooltip
// ---------------------------------------------------------------------------

import React, { useCallback, useState, useEffect, useRef } from 'react';
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip } from 'recharts';
import type { MiniPostsChartProps } from './types';

function formatCompactNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toString();
}

function CustomTooltip({
  active,
  payload,
}: {
  readonly active?: boolean;
  readonly payload?: ReadonlyArray<{ readonly value: number }>;
}) {
  if (!active || !payload?.[0]) return null;

  return (
    <div className="rounded-md bg-v11-surface-hover border border-v11-border px-2 py-1 shadow-lg">
      <span className="text-xs font-medium text-v11-text-primary tabular-nums">
        {formatCompactNumber(payload[0].value)}
      </span>
    </div>
  );
}

export function MiniPostsChart({ data, isLoading }: MiniPostsChartProps) {
  const hasData = data.length > 0;

  // Increment chartKey whenever data content changes to force LineChart remount
  // inside the stable ResponsiveContainer. This triggers the draw animation.
  const [chartKey, setChartKey] = useState(0);
  const prevDataRef = useRef(data);
  useEffect(() => {
    if (data !== prevDataRef.current) {
      prevDataRef.current = data;
      setChartKey(k => k + 1);
    }
  }, [data]);

  const labelCount = Math.min(6, data.length || 1);
  const stride = Math.max(1, Math.floor((data.length || 1) / labelCount));

  const tickFormatter = useCallback(
    (_value: string, index: number) => {
      const entry = data[index];
      if (!entry) return '';
      const isFirst = index === 0;
      const isLast = index === data.length - 1;
      const onStride = index % stride === 0;
      if (!(isFirst || isLast || onStride)) return '';
      return entry.day;
    },
    [data, stride]
  );

  return (
    <ResponsiveContainer width="100%" height={160}>
      {hasData ? (
        <LineChart key={chartKey} data={[...data]} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <YAxis
            domain={[0, 'dataMax']}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 8, fill: 'hsl(var(--v11-text-muted))' }}
            tickFormatter={formatCompactNumber}
            width={32}
            tickCount={3}
          />
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 9, fill: 'hsl(var(--v11-text-muted))' }}
            tickFormatter={tickFormatter}
            interval={0}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: 'rgba(129, 140, 248, 0.3)', strokeWidth: 1 }}
          />
          <Line
            type="monotone"
            dataKey="posts"
            stroke="#818cf8"
            strokeWidth={2}
            dot={false}
            isAnimationActive={true}
            animationBegin={150}
            animationDuration={1500}
            animationEasing="ease-out"
            activeDot={{
              r: 3,
              fill: '#818cf8',
              stroke: '#1e1b4b',
              strokeWidth: 2,
            }}
          />
        </LineChart>
      ) : (
        <LineChart data={[]} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} key="empty">
          <Line type="monotone" dataKey="posts" stroke="transparent" />
        </LineChart>
      )}
    </ResponsiveContainer>
  );
}
